/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// 3rd party libs
import React, { ReactElement, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import theme, { themeColors } from 'themes';
import { isEmpty } from 'lodash';

// Components
import { Checkbox, InformationTooltip, TooltipWrapper } from 'components';

// User Management Components
import {
  BulkUser,
  Group,
  User,
  UserGroup,
  UserManagementTableType,
  UserTypes,
  ValidatedUsersResponse
} from 'types/user-management';
import { TFunction } from 'i18next';
// Types
import { ChangeEvent, TableColumnConfigs } from 'types';
import { ColumnConfig } from '../../../components/NewBaseTable/NewBaseTable';
import { CellContext } from '@tanstack/react-table';
import { PencilIcon } from 'icons/indexed-icons/PencilIcon';
import { AddUserIcon } from 'icons/indexed-icons/AddUserIcon';
import { GroupAddIcon } from 'icons/indexed-icons/GroupAddIcon';
import { TableAction } from '..';
import { DropDownItem } from 'pages/ProteinMachine/MachineConfig/Common/Alerts/FormElementsTypes';
import Select, { MultiValue } from 'react-select';
import { Link, useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { IcoExclamationCircleHollow } from 'icons/indexed-icons/IcoExclamationCircleHollow';
import DropDownMultiRadioSelect, {
  OptionType
} from 'components/DropDownMultiRadioSelect/DropDownMultiRadioSelect';
import breakpoints from 'constants/breakpoints';
import { CustomSelect as GroupSelect } from 'components';
import { TextEllipsis } from 'common/components/common-styled';
import { PageSelection } from './UserManagementTable';
import { alphaNumericNameWithSpaceAndHyphen } from 'common/helpers/v1';

//props def
interface DynamicInputProps {
  variant?: string;
  borderVariant?: string;
  id?: string;
}
// Styling
const TableCellWrapper = styled.div`
  display: flex;
`;
const UserGroupCellWrapper = styled.div`
  display: flex;
  @media (min-width: ${breakpoints.size.md}px) {
    min-width: 110px;
  }
`;
const TableCellItemWrapper = styled.div`
  min-width: 140px;
  padding-left: 4px;
`;
const TableCell = styled.div<{ display?: string; overflow?: string }>`
  display: ${({ display }) => display};
  align-items: center;
  gap: 0.3rem;
  width: 12rem;
  overflow: ${({ overflow }) => overflow || 'hidden'};
  text-overflow: ellipsis;
  white-space: nowrap;
  word-wrap: break-word;
  align-items: center;
`;
const TableCellSharedData = styled.div`
  width: 2rem;
`;
const TableCellButton = styled.div`
  align-items: right;
  cursor: pointer;
  margin-right: 0.25rem;
`;
const TableDataLink = styled.div`
  color: ${theme.colors.primaryBlue5};
  cursor: pointer;
  a {
    text-decoration: none;
    color: ${theme.colors.primaryBlue5};
  }
`;
const CustomGroupSelect = styled.span`
  width: 15rem;
  z-index: 500;
  margin-bottom: 1rem;
  padding-top: 1rem;
`;
const CustomSelect = styled.span`
  width: 12rem;
  z-index: 500;
  margin-bottom: 1rem;
  padding-top: 1rem;
`;
const IcoWrapper = styled.div`
  position: absolute;
  display: flex;
  gap: 0.313rem;
  margin-top: 0.25rem;
`;
const ErrorMessageWrapper = styled.div`
  color: #b62c10;
  font-family: Roboto;
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 600;
  width: 12rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ErrorMessage = styled.div<{ hasMessage: string | undefined }>`
  position: relative;
  color: #ff0000;
  cursor: default;
  font-size: 0.8rem;
  display: ${(props) => {
    if (props.hasMessage && props.hasMessage === 'error') {
      return 'inline';
    } else {
      return 'none';
    }
  }};
`;

const RowItem = styled.div`
  width: 230px;
  margin-bottom: 1rem;
  padding-top: 1rem;
`;
const DynamicInput = styled.input<DynamicInputProps>`
  width: 100%;
  height: 2.5rem;
  padding: 0 1rem;

  font-family: ${(props) => props.theme.typography.family || 'sans-serif'};
  font-size: ${(props) => props.theme.typography.components.input.size || '0.875rem'};
  line-height: ${(props) => props.theme.typography.components.input.lineHeight || '1.125rem'};
  font-weight: ${(props) => props.theme.typography.components.input.weight || '500'};
  color: ${(props) => {
    if (props.variant && props.variant === 'disabled') {
      return props.theme.colors.disabled.light.fill;
    } else if (props.variant && props.variant === 'white-dark') {
      return 'black';
    }
    return props.theme.colors.field.select.enabled || '#5D6A86';
  }};
  background-color: ${(props) =>
    props.variant
      ? props.variant === 'disabled'
        ? props.theme.colors.field.disabled.fill
        : props.variant === 'white' || props.variant === 'white-dark'
        ? props.theme.colors.field.white.fill
        : props.theme.colors.field.grey.fill || 'rgb(244, 247, 249)'
      : 'transparent'};

  cursor: ${(props) => (props.variant && props.variant === 'disabled' ? 'not-allowed' : 'auto')};

  appearance: none;

  box-sizing: border-box;
  border-radius: 0.375rem;
  border: ${(props) => {
    if (props.borderVariant && props.borderVariant === 'none') {
      return 'none';
    } else if (props.borderVariant && props.borderVariant === 'error') {
      return props.theme.colors.borders.error.border;
    }
    return props.theme.colors.borders.border02.border || '0.0625rem solid #D8DDe3';
  }};
  box-shadow: ${(props) =>
    (props.borderVariant && props.borderVariant === 'none'
      ? 'none'
      : props.theme.colors.borders.border02.shadow) || 'none'};

  ::placeholder {
    color: ${(props) => props.theme.colors.mediumGrey3};
  }

  :focus {
    outline: none;
    border: 1px solid ${themeColors.primaryBlue5};
  }

  :hover {
    outline: none;
    border-color: ${theme.colors.primaryBlue5} !important;
  }
`;

export const CustomUserHeader = styled.thead(({ theme }) => ({
  tr: {
    background: theme.colors.lightGrey1,
    th: {
      fontSize: '0.8125rem',
      fontWeight: 'bold',
      lineHeight: theme.typography.components.tableHeader.lineHeight,
      borderBottom: theme.colors.borders.border02.border,
      padding: '0',
      height: '7.5rem',
      ':first-child': {
        borderTopLeftRadius: '0.5rem'
      },
      ':last-child': {
        borderTopRightRadius: '0.5rem'
      }
    }
  }
}));

export const CustomGroupHeader = styled.thead(({ theme }) => ({
  tr: {
    background: theme.colors.lightGrey1,
    th: {
      fontSize: '0.8125rem',
      fontWeight: 'bold',
      lineHeight: theme.typography.components.tableHeader.lineHeight,
      borderBottom: theme.colors.borders.border02.border,
      padding: '0.75rem',
      height: '4.625rem',
      ':first-child': {
        borderTopLeftRadius: '0.5rem'
      },
      ':last-child': {
        borderTopRightRadius: '0.5rem'
      }
    }
  }
}));
export const groupsColumnConfigs = (
  onClickButton: (tableType: UserManagementTableType, id?: string, selectedGroup?: Group) => void,
  t: TFunction<'fpns'[], undefined>
): ColumnConfig[] => {
  const history = useHistory();
  return [
    {
      header: t('mm_group_table_name') as string,
      id: 'name',
      isEnableSorting: true,
      isSelected: true,
      isAlwaysVisible: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const data = cellValue.row.original;
        return (
          <TableDataLink>
            <Link to={`${history.location.pathname}/${data.id}`}>{cellValue.getValue()}</Link>
          </TableDataLink>
        );
      }
    },
    {
      header: t('mm_group_table_customer_location') as string,
      id: 'customer',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as Group;
        const count = (row && row.customer) ?? 0;
        return <TableCell>{row && row.allOrganizations ? 'All' : count}</TableCell>;
      }
    },
    {
      header: t('mm_group_table_number_users') as string,
      id: 'numberOfUsers',
      isEnableSorting: true,
      isSelected: true,
      isAlwaysVisible: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as Group;
        return (
          <TableCellWrapper>
            <TableCellSharedData>{cellValue.getValue()}</TableCellSharedData>
            <TableCellButton
              onClick={() => {
                onClickButton(UserManagementTableType.USER, undefined, row);
              }}
            >
              <TooltipWrapper
                Tooltip={'Add new user to this group'}
                textColor="#323130"
                backGroundColor="#ffffff"
              >
                <AddUserIcon />
              </TooltipWrapper>
            </TableCellButton>
          </TableCellWrapper>
        );
      }
    },
    {
      header: t('mm_group_table_access_group') as string,
      id: 'accessGroup',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        return <TableCell>{cellValue.getValue() ? 'Internal' : 'External'}</TableCell>;
      }
    },
    {
      header: t('mm_group_table_edit_group') as string,
      id: 'editGroup',
      isEnableSorting: false,
      isSelected: true,
      isAlwaysVisible: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const group = cellValue.row.original as Group;
        return (
          <TableCellButton
            onClick={() => {
              onClickButton(UserManagementTableType.GROUP, group.id);
            }}
          >
            <TooltipWrapper
              Tooltip={'Edit this group'}
              textColor="#323130"
              backGroundColor="#ffffff"
            >
              <PencilIcon width={20} height={20} />
            </TooltipWrapper>
          </TableCellButton>
        );
      }
    }
  ];
};
const getColumnCheck = (
  handleClick?: (action?: string) => void,
  isDisable?: boolean,
  forceReset?: boolean,
  setResetControlCheck?: (val: boolean) => void
): ReactElement => {
  const [isChecked, setIsChecked] = useState(false);
  if (forceReset && isChecked) {
    setIsChecked(false);
    setResetControlCheck?.(false);
  }
  return (
    <div>
      <Checkbox
        width={18}
        height={18}
        checked={isChecked}
        onChange={() => {
          setIsChecked(!isChecked);
          handleClick?.(isChecked ? 'deselectAll' : 'selectAll');
        }}
        margin="0"
        disabled={isDisable}
      />
    </div>
  );
};
const getColumnCheckByPage = (
  handleClick?: (action?: string) => void,
  selectedPage?: PageSelection
): ReactElement => {
  const [isChecked, setIsChecked] = useState(false);

  if (isChecked && selectedPage?.isPartialSelection) {
    setIsChecked(false);
  } else if (
    !isChecked &&
    selectedPage?.selectedItems &&
    selectedPage?.selectedItems.length > 0 &&
    !selectedPage?.isPartialSelection
  ) {
    setIsChecked(true);
  } else if (isChecked && !selectedPage) {
    setIsChecked(false);
  }
  return (
    <div>
      <Checkbox
        width={18}
        height={18}
        checked={isChecked}
        onChange={() => {
          setIsChecked(!isChecked);
          handleClick?.(isChecked ? 'deselectAll' : 'selectAll');
        }}
        margin="0"
      />
    </div>
  );
};
export const usersValidationColumnConfigs = (
  handleRoleOptionSelect: (id: string, option?: OptionType, parentOption?: OptionType) => void,
  handleMultiSelect: (field: string, items: MultiValue<DropDownItem>, id: string) => void,
  validateOnBlur: (field: string, value: string, id: string) => void,
  t: TFunction<'fpns'[], undefined>,
  updateFieldOnChange: (field: string, value: string, post: number, id: string) => void,
  handleCheckClickOnBulkMode?: (action?: string, data?: UserTypes) => void,
  selectedTableItems?: UserTypes[]
): ColumnConfig[] => {
  return [
    {
      id: 'select',
      header: getColumnCheck(handleCheckClickOnBulkMode, false),
      isEnableSorting: false,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as UserTypes;
        return (
          <div>
            <Checkbox
              width={18}
              height={18}
              margin="0"
              checked={!!selectedTableItems?.find((user) => user?.id === row.id)}
              onChange={() =>
                handleCheckClickOnBulkMode && handleCheckClickOnBulkMode('selectUser', row)
              }
            />
          </div>
        );
      }
    },
    {
      header: t('first_name', { ns: 'common' }) as string,
      id: 'firstName',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as BulkUser;
        const firstName = (row && row.firstName) ?? '';
        const id = (row && row.id) ?? '';
        const errors = (row && row.errorMessages) ?? '';
        const columnError = getColumnError(errors, 'first_name');
        const ifocus = ((row && row.firstNameFocus) as boolean) ?? false;
        const select = ((row && row.selectionStart) as number) ?? 0;
        const inputRef = useRef<HTMLInputElement>(null);
        useLayoutEffect(() => {
          if (inputRef.current && typeof select === 'number') {
            inputRef.current.setSelectionRange(select, select);
          }
        }, [select, firstName]);
        return (
          <RowItem>
            <DynamicInput
              ref={inputRef}
              variant="white"
              type="text"
              value={firstName}
              placeholder={t('first_name', { ns: 'common' }) as string}
              width="auto"
              onChange={(event: ChangeEvent) => {
                const { value } = event.target;
                const inputValue =
                  value.length < firstName.length
                    ? value
                    : alphaNumericNameWithSpaceAndHyphen(value)
                    ? value
                    : firstName;
                updateFieldOnChange(
                  'firstName',
                  inputValue,
                  event.target.selectionStart as number,
                  id
                );
                validateOnBlur('first_name', inputValue, id);
              }}
              autoFocus={ifocus}
            />
            {columnError && (
              <ErrorMessage hasMessage={'error'}>
                <IcoWrapper>
                  <IcoExclamationCircleHollow />
                  <ErrorMessageWrapper>{columnError}</ErrorMessageWrapper>
                </IcoWrapper>
              </ErrorMessage>
            )}
          </RowItem>
        );
      }
    },
    {
      header: t('last_name', { ns: 'common' }) as string,
      id: 'lastName',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as BulkUser;
        const lastName = (row && row.lastName) ?? '';
        const id = (row && row.id) ?? '';
        const errors = (row && row.errorMessages) ?? '';
        const columnError = getColumnError(errors, 'last_name');
        const ifocus = ((row && row.lastNameFocus) as boolean) ?? false;
        const select = ((row && row.selectionStart) as number) ?? 0;
        const inputRef = useRef<HTMLInputElement>(null);
        useLayoutEffect(() => {
          if (inputRef.current && typeof select === 'number') {
            inputRef.current.setSelectionRange(select, select);
          }
        }, [select, lastName]);
        return (
          <RowItem>
            <DynamicInput
              ref={inputRef}
              variant="white"
              value={lastName}
              placeholder={t('last_name', { ns: 'common' }) as string}
              width="auto"
              onChange={(event: ChangeEvent) => {
                const { value } = event.target;
                const inputValue =
                  value.length < lastName.length
                    ? value
                    : alphaNumericNameWithSpaceAndHyphen(value)
                    ? value
                    : lastName;
                updateFieldOnChange(
                  'lastName',
                  inputValue,
                  event.target.selectionStart as number,
                  id
                );
                validateOnBlur('last_name', inputValue, id);
              }}
              borderVariant={columnError ? 'error' : undefined}
              autoFocus={ifocus}
            />
            {columnError && (
              <ErrorMessage hasMessage={'error'}>
                <IcoWrapper>
                  <IcoExclamationCircleHollow />
                  <ErrorMessageWrapper>{columnError}</ErrorMessageWrapper>
                </IcoWrapper>
              </ErrorMessage>
            )}
          </RowItem>
        );
      }
    },
    {
      header: t('mm_user_table_email') as string,
      id: 'email',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as BulkUser;
        const id = (row && row.id) ?? '';
        const email = (row && row.email) ?? '';
        const errors = (row && row.errorMessages) ?? '';
        const columnError = getColumnError(errors, 'email');
        const ifocus = ((row && row.emailFocus) as boolean) ?? false;
        const select = ((row && row.selectionStart) as number) ?? 0;
        const inputRef = useRef<HTMLInputElement>(null);
        useLayoutEffect(() => {
          if (inputRef.current && typeof select === 'number') {
            inputRef.current.setSelectionRange(select, select);
          }
        }, [select, email]);
        return (
          <RowItem>
            <DynamicInput
              ref={inputRef}
              variant="white"
              placeholder={t('mm_user_table_email') as string}
              width="auto"
              value={email}
              onChange={(event: ChangeEvent) => {
                updateFieldOnChange(
                  'email',
                  event.target.value,
                  event.target.selectionStart as number,
                  id
                );
                validateOnBlur('email', event.target.value, id);
              }}
              borderVariant={columnError ? 'error' : undefined}
              autoFocus={ifocus}
            />
            {columnError && (
              <ErrorMessage hasMessage={'error'}>
                <IcoWrapper>
                  <IcoExclamationCircleHollow />
                  <ErrorMessageWrapper>{columnError}</ErrorMessageWrapper>
                </IcoWrapper>
              </ErrorMessage>
            )}
          </RowItem>
        );
      }
    },
    {
      header: t('mm_user_table_phone') as string,
      id: 'phone',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as BulkUser;
        const id = (row && row.id) ?? '';
        const phone = (row && row.phone) ?? '';
        const errors = (row && row.errorMessages) ?? '';
        const columnError = getColumnError(errors, 'phone');
        const ifocus = ((row && row.phoneFocus) as boolean) ?? false;
        const select = ((row && row.selectionStart) as number) ?? 0;
        const inputRef = useRef<HTMLInputElement>(null);
        useLayoutEffect(() => {
          if (inputRef.current && typeof select === 'number') {
            inputRef.current.setSelectionRange(select, select);
          }
        }, [select, phone]);
        return (
          <RowItem>
            <DynamicInput
              ref={inputRef}
              variant="white"
              value={phone}
              placeholder={t('mm_user_table_phone') as string}
              width="auto"
              onChange={(event: ChangeEvent) => {
                updateFieldOnChange(
                  'phone',
                  event.target.value,
                  event.target.selectionStart as number,
                  id
                );
                validateOnBlur('phone', event.target.value, id);
              }}
              borderVariant={columnError ? 'error' : undefined}
              autoFocus={ifocus}
            />
            {columnError && (
              <ErrorMessage hasMessage={'error'}>
                <IcoWrapper>
                  <IcoExclamationCircleHollow />
                  <ErrorMessageWrapper>{columnError}</ErrorMessageWrapper>
                </IcoWrapper>
              </ErrorMessage>
            )}
          </RowItem>
        );
      }
    },
    {
      header: t('mm_user_table_group') as string,
      id: 'groups',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const id = cellValue.row.original.id as string;
        const groupList = cellValue.row.original.groups as DropDownItem[];
        const selectedGroups = cellValue.row.original.selectedGroups as DropDownItem[];
        const columnError = 'All fields are required';
        return (
          <CustomGroupSelect>
            <GroupSelect
              isMulti
              isCheckboxInput
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              placeholder="Select"
              value={selectedGroups}
              options={groupList}
              onChange={(items) => {
                handleMultiSelect('selectedGroups', items, id);
              }}
              menuPosition="fixed"
            />
            <ErrorMessage
              hasMessage={
                !selectedGroups || (selectedGroups && selectedGroups.length === 0)
                  ? 'error'
                  : undefined
              }
            >
              <IcoWrapper>
                <IcoExclamationCircleHollow />
                <ErrorMessageWrapper>{columnError}</ErrorMessageWrapper>
              </IcoWrapper>
            </ErrorMessage>
          </CustomGroupSelect>
        );
      }
    },
    {
      header: t('mm_user_table_role') as string,
      id: 'role',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const id = cellValue.row.original.id as string;
        const selectedGroups = cellValue.row.original.selectedGroups as DropDownItem[];

        return (
          <CustomSelect>
            <DropDownMultiRadioSelect
              userId={id}
              groups={selectedGroups}
              handleOptionSelect={handleRoleOptionSelect}
            />
          </CustomSelect>
        );
      }
    }
  ];
};

export const usersColumnConfigs = (
  handleTableAction: ({ flyoutType, flyoutItemId, action, selectedUser }: TableAction) => void,
  t: TFunction<'fpns'[], undefined>,
  handleClick?: (action?: string, data?: UserTypes) => void,
  selectedPage?: PageSelection
): ColumnConfig[] => {
  return [
    {
      id: 'action',
      header: getColumnCheckByPage(handleClick, selectedPage),
      isEnableSorting: false,
      isSelected: true,
      isAlwaysVisible: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as UserTypes;
        return (
          <div>
            <Checkbox
              width={18}
              height={18}
              margin="0"
              checked={!!selectedPage?.selectedItems?.find((user) => user?.id === row.id)}
              onChange={() => handleClick && handleClick('selectUser', row)}
            />
          </div>
        );
      }
    },
    {
      header: t('mm_user_table_name') as string,
      id: 'name',
      isEnableSorting: true,
      isSelected: true,
      isAlwaysVisible: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as UserTypes;
        const firstName = (row && row.firstName) ?? '';
        const lastName = (row && row.lastName) ?? '';
        const name = `${firstName} ${lastName}`;
        return name.length > 25 ? (
          <TableCell display={'flex'}>
            <InformationTooltip
              tooltipText={firstName || lastName ? name : '-'}
              icon={
                <TextEllipsis width="12rem"> {firstName || lastName ? name : '-'}</TextEllipsis>
              }
              placement="top"
              width="14rem"
            />
          </TableCell>
        ) : (
          <TableCell display={'flex'} title={firstName || lastName ? name : '-'}>
            {firstName || lastName ? name : '-'}
          </TableCell>
        );
      }
    },
    {
      header: t('mm_user_table_email') as string,
      id: 'email',
      isEnableSorting: true,
      isSelected: true,
      isAlwaysVisible: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const email = cellValue.getValue();
        return email.length > 20 ? (
          <TableCell display={'flex'}>
            <InformationTooltip
              tooltipText={email}
              icon={<TextEllipsis width="12rem"> {email}</TextEllipsis>}
              placement="top"
              width="14rem"
            />
          </TableCell>
        ) : (
          <TableCell title={typeof cellValue.getValue() === 'string' ? cellValue.getValue() : ''}>
            {cellValue.getValue() ?? '-'}
          </TableCell>
        );
      }
    },
    {
      header: t('mm_user_table_group') as string,
      id: 'group',
      isEnableSorting: true,
      isSelected: true,
      isAlwaysVisible: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const user = cellValue.row.original as UserTypes;
        const groups = user.groups as UserGroup[];
        return (
          <>
            {groups?.length > 0 && (
              <TooltipWrapper
                position="fixed-top"
                Tooltip={t(`click_to_edit_role`)}
                textColor={theme.colors.textLightBlack}
                backGroundColor={theme.colors.white}
              >
                <UserGroupCellWrapper
                  onClick={() => {
                    handleTableAction({
                      flyoutType: UserManagementTableType.USER,
                      action: 'edit',
                      flyoutItemId: user.id,
                      selectedUser: user
                    });
                  }}
                >
                  <TableCellButton>
                    <GroupAddIcon />
                  </TableCellButton>
                  <TableDataLink>
                    {groups?.length} {groups?.length > 1 ? 'Groups' : 'Group'}
                  </TableDataLink>
                </UserGroupCellWrapper>
              </TooltipWrapper>
            )}
          </>
        );
      }
    },
    {
      header: t('mm_user_table_status') as string,
      id: 'status',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const data = cellValue.row.original as UserTypes;
        console.log('data', data);
        let isActive = data.isActive;
        const handleOptionSelect = (option?: DropDownItem | null) => {
          isActive = option?.value === 'active';
          handleClick && handleClick('changeStatus', { id: data.id, isActive: isActive });
        };
        return (
          <TableCellItemWrapper>
            <Select
              isSearchable={false}
              options={[
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' }
              ]}
              menuPlacement="auto"
              menuPosition="fixed"
              menuShouldBlockScroll={true}
              onChange={(option) => handleOptionSelect(option)}
              value={
                isActive
                  ? { label: 'Active', value: 'active' }
                  : { label: 'Inactive', value: 'inactive' }
              }
              components={{
                IndicatorSeparator: () => null
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  border: 'none',
                  boxShadow: 'none',
                  width: '8rem',
                  marginLeft: '-0.8rem'
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  color: themeColors.lightGrey8,
                  '&:hover': {
                    color: themeColors.lightGrey8
                  }
                }),
                option: (base, { isSelected }) => ({
                  ...base,
                  backgroundColor: isSelected ? themeColors.primaryBlue5 : themeColors.white
                })
              }}
            />
          </TableCellItemWrapper>
        );
      }
    },
    {
      header: t('mm_user_table_phone') as string,
      id: 'phone',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        return <TableCell>{cellValue.getValue() ?? '-'}</TableCell>;
      }
    },
    {
      header: t('mm_user_table_alert') as string,
      id: 'alertNotification',
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const row = cellValue.row.original as User;
        const textAlert = row && row.textAlert ? 'Text' : '';
        const emailAlert = row && row.emailAlert ? 'Email' : '';
        const pushAppEnabled = row && row.pushAppEnabled ? 'Push App' : '';
        return (
          <TableCell>
            {textAlert}
            {!isEmpty(textAlert) && (!isEmpty(emailAlert) || !isEmpty(pushAppEnabled)) && ', '}
            {emailAlert}
            {!isEmpty(emailAlert) && !isEmpty(pushAppEnabled) && ', '}
            {pushAppEnabled}
            {isEmpty(textAlert) && isEmpty(emailAlert) && isEmpty(pushAppEnabled) && '-'}
          </TableCell>
        );
      }
    },
    {
      header: t('mm_user_table_edit_user') as string,
      id: 'editUser',
      isEnableSorting: false,
      isSelected: true,
      isAlwaysVisible: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const user = cellValue.row.original as UserTypes;
        return (
          <TableCellButton
            onClick={() => {
              handleTableAction({
                flyoutType: UserManagementTableType.USER,
                action: 'editUser',
                flyoutItemId: user.id,
                selectedUser: user
              });
            }}
          >
            <TooltipWrapper
              Tooltip={'Edit this user'}
              textColor="#323130"
              backGroundColor="#ffffff"
            >
              <PencilIcon width={20} height={20} />
            </TooltipWrapper>
          </TableCellButton>
        );
      }
    }
  ];
};

export function getFilteredData(data: (UserTypes | User)[], searchKey: string) {
  switch (searchKey) {
    case 'admin':
      return data.filter((user) => user.isAdmin);
    case 'member':
      return data.filter((user) => !user.isAdmin);
    case 'active':
      return data.filter((user) => user.isActive);
    case 'inactive':
      return data.filter((user) => !user.isActive);
    default:
      return data;
  }
}

export const addKeyProp = (
  data: (Group | User | BulkUser | ValidatedUsersResponse | UserTypes)[],
  type: UserManagementTableType
): (Group | User | BulkUser | ValidatedUsersResponse)[] =>
  data.map((d, i) => {
    return { ...d, key: `${type}-${i}` };
  });
export const addIdProp = (
  data: (Group | User | BulkUser | ValidatedUsersResponse)[]
): (Group | User | BulkUser | ValidatedUsersResponse)[] =>
  data.map((d) => {
    const v = uuidv4();
    return { ...d, id: v };
  });
export const transformKeysToTableColumn = (
  groups: Group[],
  type: string
): {
  key: string;
  name: string;
  accessGroup: boolean;
  customer: number;
  numberOfUsers: number;
}[] => {
  const tg: {
    key: string;
    id: string;
    name: string;
    accessGroup: boolean;
    customer: number;
    numberOfUsers: number;
  }[] = [];

  groups.forEach((g, i) => {
    const tfm = {
      key: `${type}-${i}`,
      id: g.id,
      name: g.name,
      accessGroup: g.isInternal as boolean,
      customer: g.organizationsCount,
      numberOfUsers: g.usersCount
    };
    tg.push(tfm);
  });
  return tg;
};
export const updateValidatedUser = (
  users: ValidatedUsersResponse[] | undefined,
  field: string,
  value: string,
  pos: number,
  id: string
): ValidatedUsersResponse[] | undefined => {
  if (!users) return [];
  for (const user of users) {
    //turn off existing focus
    if (user['firstNameFocus']) user['firstNameFocus'] = false;
    if (user['lastNameFocus']) user['lastNameFocus'] = false;
    if (user['emailFocus']) user['emailFocus'] = false;
    if (user['phoneFocus']) user['phoneFocus'] = false;
  }
  for (const user of users) {
    //turn on focus on current item
    if (id === user.id) {
      user[field] = value;
      user[field + 'Focus'] = true;
      user['selectionStart'] = pos;
      break;
    }
  }
  return users;
};
export const updateMultiSelect = (
  users: ValidatedUsersResponse[] | undefined,
  field: string,
  options: MultiValue<DropDownItem>,
  id: string
): ValidatedUsersResponse[] | undefined => {
  if (!users) return [];
  for (const user of users) {
    if (id === user.id) {
      const opts = JSON.parse(JSON.stringify(options));
      user[field] = opts;
      break;
    }
  }
  return users;
};
export const updateSelect = (
  users: ValidatedUsersResponse[] | undefined,
  id: string,
  option?: OptionType,
  parentOption?: OptionType
): ValidatedUsersResponse[] | undefined => {
  if (!users) return [];
  let rowItemFound = false;
  const { value } = parentOption as OptionType;
  for (const user of users) {
    if (user.id === id) {
      rowItemFound = true;
      for (const group of user.selectedGroups as DropDownItem[]) {
        if (group.value === value) {
          group.isAdmin = option && option.value === 'admin' ? true : false;
          break;
        }
      }
    }
    if (rowItemFound) break;
  }
  return users;
};
export const updateAlertSelect = (
  users: ValidatedUsersResponse[] | undefined,
  id: string,
  option?: OptionType,
  parentOption?: OptionType
): ValidatedUsersResponse[] | undefined => {
  if (!users) return [];

  let rowItemFound = false;
  const { value } = parentOption as OptionType;
  for (const user of users) {
    if (user.id === id) {
      rowItemFound = true;
      for (const group of user.selectedGroups as DropDownItem[]) {
        if (group.value === value) {
          if (option && option.value === 'text') {
            group.isTextAlert = !option.isChecked;
          } else if (option && option.value === 'email') {
            group.isEmailAlert = !option.isChecked;
          }
          break;
        }
      }
    }
    if (rowItemFound) break;
  }
  return users;
};
const getColumnError = (
  errors: { columnName: string; errors: string[] }[],
  col: string
): string | undefined => {
  if (errors.length === 0 || errors[0].errors.length === 0) return undefined;
  const error = errors.find((err) => err.columnName === col);
  return error?.errors[0] as string;
};
export const transformUserColumnOptions = (users: UserTypes[], type: string) => {
  return users?.map((user, i) => {
    const tfm = {
      ...user,
      key: `${type}-${i}`,
      id: user.id,
      name: `${user.firstName || ''} ${user.lastName || ''}`,
      email: user.email,
      group: user.groups,
      role: user.isAdmin,
      status: user.isActive,
      phone: user.phone,
      alertNotification: {
        emailAlert: user.emailAlert,
        textAlert: user.textAlert
      }
    };
    return tfm;
  });
};

export const transfromUserOptions = (userList: UserTypes[], addedUser: string[]) => {
  const userOption = userList.map((user) => ({
    label: `${user.firstName || ''} ${user.lastName}`,
    value: user,
    isDisabled: addedUser.includes(user.id || '')
  }));
  return userOption;
};

export const userNameValidation = (
  userList: ValidatedUsersResponse[]
): ValidatedUsersResponse[] => {
  return userList.map((user) => {
    const newUser: ValidatedUsersResponse = { ...user };

    // Deep clone existing errors
    const existingErrors = Array.isArray(user.errorMessages)
      ? user.errorMessages.map((e) => ({ ...e, errors: [...e.errors] }))
      : [];

    const isValidFirstName = alphaNumericNameWithSpaceAndHyphen(user?.firstName || '');
    const isValidLastName = alphaNumericNameWithSpaceAndHyphen(user?.lastName || '');

    if (!isValidFirstName) {
      addOrUpdateError(existingErrors, 'first_name', 'Name is not valid');
    }
    if (!isValidLastName) {
      addOrUpdateError(existingErrors, 'last_name', 'Name is not valid');
    }
    newUser.errorMessages = existingErrors;
    return newUser;
  });
};

// merge the errors according to same column name
const addOrUpdateError = (
  errors: { columnName: string; errors: string[] }[],
  column: string,
  message: string
) => {
  const existing = errors.find((err) => err.columnName === column);
  if (existing) {
    if (!existing.errors.includes(message)) {
      existing.errors.push(message);
    }
  } else {
    errors.push({ columnName: column, errors: [message] });
  }
};
